import { createContext, createElement, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";

const Ctx = createContext(null);

export function TerminalProvider({ children }) {
  const api = useApi();
  const [wallet, setWallet] = useState(null);
  const [settings, setSettings] = useState({ buy_presets: [0.001, 0.005, 0.01, 0.05], sell_presets: [25, 50, 100], slippage_bps: 1500 });
  const [quickAmount, setQuickAmount] = useState(() => Number(localStorage.getItem("quickAmount") || 0.001));
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState({});

  const refreshWallet = useCallback(async () => {
    try { setWallet(await api("get", "/wallet")); }
    catch (e) { if (e?.response?.status === 403) setForbidden(true); }
  }, [api]);

  useEffect(() => {
    refreshWallet();
    api("get", "/settings").then(setSettings).catch(() => {});
    const t = setInterval(refreshWallet, 15000);
    return () => clearInterval(t);
  }, [api, refreshWallet]);

  useEffect(() => { localStorage.setItem("quickAmount", String(quickAmount)); }, [quickAmount]);

  const buy = useCallback(async (token, amount, symbol) => {
    const key = `buy-${token}`;
    setBusy((b) => ({ ...b, [key]: true }));
    const id = toast.loading(`${symbol || "Token"} alınıyor · ${amount} ETH`);
    try {
      const r = await api("post", "/trade/buy", { token, amount_eth: Number(amount) });
      const failed = r.results.filter((x) => !x.ok);
      toast.success(`${r.symbol} alındı · ${r.ok_count}/${r.total} cüzdan · ${amount} ETH`, { id, description: failed.map((x) => `${x.wallet}: ${x.error}`).join(" | ") || undefined });
      refreshWallet();
      return r;
    } catch (e) { toast.error(errMsg(e), { id }); }
    finally { setBusy((b) => ({ ...b, [key]: false })); }
  }, [api, refreshWallet]);

  const sell = useCallback(async (token, percent, symbol, walletId) => {
    const key = walletId ? `sell-${token}-${walletId}` : `sell-${token}`;
    setBusy((b) => ({ ...b, [key]: true }));
    const id = toast.loading(`${symbol || "Token"} %${percent} satılıyor`);
    try {
      const r = await api("post", "/trade/sell", { token, percent: Number(percent), wallet_id: walletId });
      const failed = r.results.filter((x) => !x.ok);
      toast.success(`${r.symbol} satıldı · ${r.ok_count}/${r.total} cüzdan · ${Number(r.eth).toFixed(5)} ETH`, { id, description: failed.map((x) => `${x.wallet}: ${x.error}`).join(" | ") || undefined });
      refreshWallet();
      return r;
    } catch (e) { toast.error(errMsg(e), { id }); }
    finally { setBusy((b) => ({ ...b, [key]: false })); }
  }, [api, refreshWallet]);

  const value = { api, wallet, refreshWallet, settings, setSettings, quickAmount, setQuickAmount, buy, sell, busy, forbidden };
  return createElement(Ctx.Provider, { value }, children);
}

export const useTerminal = () => useContext(Ctx);
